import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../../utils/Utils.js';
import ProjectDropdown from '../dropdowns/ProjectDropdown.js';
import LotNavRow from './LotNavRow.js';
import { useCurrencySymbol, formatBalance } from '../../finances/currency.js';
import './LotProject.css';

// LotProjects is the lot page's Projects section (all lot types). A lot can
// be in several finance projects (one per IATF round, a feed project…), so
// it lists each linked project — a row that opens it, with its balance — and
// lets you link another (POST /api/lots/:lotId/projects) or remove one
// (DELETE /api/lots/:lotId/projects/:projectId). A project can cover several
// lots, so each balance is the project's, not this lot's alone. Removing a
// project never touches its transactions.
const LotProjects = ({ lot, onChange }) => {
  const { t }  = useTranslation();
  const symbol = useCurrencySymbol();
  const [projects,  setProjects]  = useState(null); // null = loading
  const [adding,    setAdding]    = useState(false);
  const [projectId, setProjectId] = useState('');
  const [error,     setError]     = useState('');

  useEffect(() => {
    Fetch('api/finance/projects', 'GET', null, (data) => setProjects(data || []), () => setProjects([]));
  }, []);

  const linkedIds  = lot.project_ids || [];
  const archived   = lot.status === 'archived';
  // Active projects this lot isn't in yet — the only ones worth offering.
  const available  = (projects || []).filter(p => p.status !== 'closed' && !linkedIds.includes(p._id));
  const hasActive  = (projects || []).some(p => p.status !== 'closed');

  const cancel = () => {
    setAdding(false);
    setError('');
    setProjectId('');
  };

  const add = () => {
    if (!projectId) return;
    Fetch(`api/lots/${lot.lot_id}/projects`, 'POST', JSON.stringify({ project_id: projectId }),
      (data) => {
        cancel();
        onChange?.(data?.project_ids || [...linkedIds, projectId]);
      },
      (message) => setError(message || t('lot_project_link_error'))
    );
  };

  const remove = (id) => {
    Fetch(`api/lots/${lot.lot_id}/projects/${id}`, 'DELETE', null,
      (data) => {
        setError('');
        onChange?.(data?.project_ids || linkedIds.filter(p => p !== id));
      },
      (message) => setError(message || t('lot_project_unlink_error'))
    );
  };

  const header = (
    <div className="lot-project-header">
      <h3>{t('projects')}</h3>
      {!adding && !archived && projects !== null && available.length > 0 && (
        <button className="lot-project-btn" onClick={() => setAdding(true)} data-testid="lot-project-add-btn">
          {t('lists_link_project')}
        </button>
      )}
    </div>
  );

  let body;
  if (linkedIds.length > 0) {
    body = (
      <div className="wld-nav-list">
        {linkedIds.map(id => {
          const p = (projects || []).find(x => x._id === id);
          return (
            <div className="lot-project-row" key={id} data-testid="lot-project-row">
              <div className="lot-project-row-link">
                <LotNavRow
                  label={p ? `${p.code} — ${p.name}` : '…'}
                  summary={p ? `${t('project_balance')}: ${formatBalance(p.balance, symbol)}` : null}
                  to={`/finances/projects/${id}`} />
              </div>
              {!archived && (
                <button className="lot-project-remove" onClick={() => remove(id)}
                  title={t('remove')} aria-label={t('remove')} data-testid="lot-project-remove-btn">×</button>
              )}
            </div>
          );
        })}
      </div>
    );
  } else {
    body = (
      <div className="lot-project-empty">
        {projects !== null && !hasActive ? t('lot_project_none_yet') : t('lot_no_project')}
      </div>
    );
  }

  return (
    <div className="lot-project" data-testid="lot-project">
      {header}
      {body}
      {adding && (
        <div className="lot-project-edit">
          <ProjectDropdown projects={available} selected={projectId} onChange={e => setProjectId(e.target.value)} />
          <button className="lot-project-btn lot-project-btn-save" onClick={add} disabled={!projectId}>{t('save')}</button>
          <button className="lot-project-btn" onClick={cancel}>{t('cancel')}</button>
          <div className="lot-project-hint">{t('lot_project_hint')}</div>
        </div>
      )}
      {error && <div className="lot-project-error">{error}</div>}
    </div>
  );
};

export default LotProjects;
